import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { EndUserType, NIGHTWEB_APP_CONSTANT, USER_STATUS } from 'src/app/constants';
import {
  ThreatPair,
  ThreatPairUser,
} from 'src/app/components/common/threat-pair-table/threat-pair-table.model';
import { ThreatPairService, ThreatPairZoneRadiiMeters, UserService } from 'src/app/services';
import {
  GeoPoint,
  bearingDegrees,
  compassDirection,
  haversineDistanceMeters,
} from 'src/app/utils';
import {
  GoogleMapComponent,
  GoogleMapParty,
  GoogleMapZoneConfig,
} from 'src/app/components/common/google-map/google-map.component';

export type AxisLinkZoneState = 'outside' | 'yellow' | 'red' | 'unknown';

export type ProximityTrend = 'closing' | 'opening' | 'steady' | 'unknown';

export interface ThreatPairPartySummary {
  role: 'Survivor' | 'Perpetrator';
  name: string;
  position: GeoPoint | null;
  headingLabel: string | null;
  /** Raw bearing (0-360, 0 = North) backing `headingLabel` — FR-402's on-map arrow needs this. */
  headingDegrees: number | null;
  /**
   * Heuristic only: this codebase does not currently model a watch's GPS
   * reporting cadence, so "more frequent reporting" is inferred from the
   * `isEmergencyState` flag already returned on last-location rows.
   */
  reportingLabel: string;
  isHighFrequency: boolean;
  lastUpdatedLabel: string;
  hasLocation: boolean;
  /** FR-502: true once this party's latest fix is older than the (provisional) staleness threshold. */
  isStale: boolean;
}

const SURVIVOR_HEADING_COLOR = '#0d6efd';
const PERPETRATOR_HEADING_COLOR = '#dc3545';
/**
 * GPS accuracy isn't modeled anywhere in this codebase yet (no real or mocked field carries it).
 * Figma's telemetry panel calls for one, so this is a fixed mock display value — not derived from
 * anything — clearly separate from the rest of the (at least computed) mock data.
 */
const MOCK_GPS_ACCURACY_LABEL = '±4 m';
/** Newest-first session log of zone-state transitions; capped so it can't grow unbounded. */
const EVENT_LOG_MAX_ENTRIES = 20;

/**
 * Google's well-known public "Night" map style (widely published, not proprietary) — gives the
 * map a dark basemap to match the Figma redesign without needing a Cloud-console-managed Map ID.
 */
const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#212529' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212529' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#a9a9b3' }] },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#c9c9d1' }],
  },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#7c7c86' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1c2a1f' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38383f' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212529' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8b8b94' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4a4a53' }] },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#212529' }],
  },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2c2c33' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
];

/**
 * Google's well-known public "Standard" light map style (widely published, not proprietary) —
 * the light-mode counterpart to `DARK_MAP_STYLES` above. A caller can't just omit `mapStyles` to
 * fall back to Google's own default light basemap: newer vector-rendered maps pick their
 * light/dark scheme from the browser's colour-scheme preference (via `colorScheme`, which can
 * only be set once at map creation, not toggled afterwards), so on a system in dark mode that
 * fallback renders dark too. An explicit style array applies immediately either way.
 */
const LIGHT_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9d9e8' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

@Component({
  selector: 'app-threat-pair-live-map-page',
  templateUrl: './threat-pair-live-map-page.component.html',
  styleUrls: ['./threat-pair-live-map-page.component.scss'],
  standalone: false,
})
export class ThreatPairLiveMapPageComponent implements OnInit, OnDestroy {
  // "Expand" grows the map within the existing page — not the real Fullscreen API. That API
  // hides everything outside its target element, including the app's own left sidebar, which
  // isn't wanted here; an in-page CSS toggle keeps the sidebar/header and just frees up the
  // counterpart card's width for the map instead.
  @ViewChild(GoogleMapComponent) googleMapRef?: GoogleMapComponent;
  isExpanded = false;
  isDarkMode = true;

  endUserId!: string;
  threatPairId!: string;
  user: any = null;
  pair: ThreatPair | null = null;

  loading = false;
  errorMessage: string | null = null;

  mapParties: GoogleMapParty[] = [];
  survivorSummary: ThreatPairPartySummary | null = null;
  perpetratorSummary: ThreatPairPartySummary | null = null;

  distanceMeters: number | null = null;
  distanceLabel = '—';
  proximityTrend: ProximityTrend = 'unknown';
  closingRateMps: number | null = null;
  isHoldingStaleFix = false;

  zoneConfig: GoogleMapZoneConfig | null = null;
  zoneState: AxisLinkZoneState = 'unknown';

  eventLog: string[] = [];

  readonly darkMapStyles = DARK_MAP_STYLES;
  readonly lightMapStyles = LIGHT_MAP_STYLES;
  readonly accuracyLabel = MOCK_GPS_ACCURACY_LABEL;
  readonly endUserTypeList = EndUserType;
  readonly userStatus = USER_STATUS;

  private previousDistanceMeters: number | null = null;
  private refreshHandle: ReturnType<typeof setInterval> | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private threatPairService: ThreatPairService,
  ) {}

  ngOnInit(): void {
    this.endUserId = this.route.snapshot.paramMap.get('id') ?? '';
    this.threatPairId = this.route.snapshot.paramMap.get('threatPairId') ?? '';
    this.loadUserAndPair();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  goBack(): void {
    this.router.navigate(['/end-users', this.endUserId]);
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    // Google Maps doesn't repaint its tiles on its own when its container resizes outside
    // Angular's [height]/[width] bindings — without this the map is left blank at the old size.
    // A tick's delay lets the CSS transition actually settle first.
    setTimeout(() => this.googleMapRef?.triggerResize(), 100);
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
  }

  get pairTitle(): string {
    if (!this.pair?.survivor || !this.pair?.perpetrator) {
      return '';
    }
    return `${this.pair.survivor.name} ↔ ${this.pair.perpetrator.name}`;
  }

  /**
   * Whichever party of the pair is *not* the end-user this page is scoped to — the profile header
   * above the map already identifies the current end-user, so only their counterpart needs a card
   * (matches the Figma mockup, which shows a single "Perpetrator" card, not one per party).
   */
  get counterpartRole(): 'Survivor' | 'Perpetrator' {
    return this.user?.userType === this.endUserTypeList.Perpetrator
      ? 'Survivor'
      : 'Perpetrator';
  }

  get counterpartUser(): ThreatPairUser | undefined {
    return this.counterpartRole === 'Survivor'
      ? this.pair?.survivor
      : this.pair?.perpetrator;
  }

  get counterpartSummary(): ThreatPairPartySummary | null {
    return this.counterpartRole === 'Survivor'
      ? this.survivorSummary
      : this.perpetratorSummary;
  }

  /**
   * AxisLink zone radii (BFRD FR-102/FR-103), in meters. Only the Red (emergency) radius is
   * modeled on the pairing today as `distanceThreshold`; Yellow (warning) is derived from it
   * until the backend adds a dedicated config field — see the constant's doc comment.
   */
  get zoneRadiiMeters(): ThreatPairZoneRadiiMeters {
    return {
      red: this.pair?.redDistanceThreshold ?? NIGHTWEB_APP_CONSTANT.axisLinkFallbackRedZoneRadiusMeters,
      yellow: this.pair?.distanceThreshold ?? NIGHTWEB_APP_CONSTANT.axisLinkFallbackYellowZoneRadiusMeters,
    };
  }

  get zoneRadiiLabel(): { yellow: string; red: string } {
    const radii = this.zoneRadiiMeters;
    return {
      yellow: `${Math.round(radii.yellow)} m`,
      red: `${Math.round(radii.red)} m`,
    };
  }

  /** Always-visible SAFE/CAUTION/ALERT status, matching the Figma pill shown in every state. */
  get statusPillLabel(): string {
    switch (this.zoneState) {
      case 'red':
        return 'ALERT';
      case 'yellow':
        return 'CAUTION';
      case 'outside':
        return 'SAFE';
      default:
        return 'UNKNOWN';
    }
  }

  // Same dark-tinted, accent-bordered treatment as the zone banner (`.live-map-banner-*` in the
  // stylesheet) — a solid Bootstrap `bg-danger`/`bg-warning`/`bg-success` pill read as a
  // disconnected design language next to the toast below it.
  get statusPillClass(): string {
    switch (this.zoneState) {
      case 'red':
        return 'live-map-status-red';
      case 'yellow':
        return 'live-map-status-yellow';
      case 'outside':
        return 'live-map-status-outside';
      default:
        return 'live-map-status-unknown';
    }
  }

  // FR-404: cadence tier is 1:1 with zone state (Outside→Normal, Yellow→Elevated, Red→High
  // per the BFRD's zone-state table), so the label names both rather than adding a second one.
  get zoneStateLabel(): string {
    switch (this.zoneState) {
      case 'red':
        return 'Red Zone · Emergency (High cadence)';
      case 'yellow':
        return 'Yellow Zone · Warning (Elevated cadence)';
      case 'outside':
        return 'Outside Monitoring Zone (Normal cadence)';
      default:
        return 'Zone unknown';
    }
  }

  /**
   * FR-502: the Protected Party's own fix — the zone centre — may itself be stale. When it is,
   * the zone read is flagged low-confidence rather than hidden (the BFRD says "flag", not
   * "hide").
   */
  get isZoneCentreStale(): boolean {
    return !!this.survivorSummary?.hasLocation && !!this.survivorSummary?.isStale;
  }

  get isInAlertZone(): boolean {
    return this.zoneState === 'red' || this.zoneState === 'yellow';
  }

  /** FR-501: say plainly when a number is being held through a stale fix rather than measured. */
  get distanceDisplayLabel(): string {
    return this.isHoldingStaleFix
      ? `${this.distanceLabel} (held)`
      : this.distanceLabel;
  }

  get closingRateLabel(): string {
    if (this.closingRateMps == null) {
      return '—';
    }
    const sign = this.closingRateMps > 0 ? '+' : '';
    return `${sign}${this.closingRateMps.toFixed(1)} m/s`;
  }

  // Split into title/detail (matching the Figma banner's two-line layout) rather than one long
  // combined string.
  get mapBannerTitle(): string {
    const name = this.perpetratorSummary?.name ?? 'Monitored subject';
    const zone = this.zoneState === 'red' ? 'Red Zone' : 'Yellow Zone';
    return `${name} within ${zone}`;
  }

  get mapBannerDetail(): string {
    const parts = [`${this.distanceDisplayLabel} away`];
    if (this.perpetratorSummary?.headingLabel) {
      parts.push(`heading ${this.perpetratorSummary.headingLabel}`);
    }
    parts.push(this.proximityTrendLabel());
    return parts.join(' · ');
  }

  proximityTrendLabel(): string {
    switch (this.proximityTrend) {
      case 'closing':
        return 'Toward you';
      case 'opening':
        return 'Moving away';
      case 'steady':
        return 'Holding steady';
      default:
        return 'Not enough data yet';
    }
  }

  // Same severity-colour language as the Status/Current distance rows above: closing (bad) reads
  // as danger, opening (good) as success, steady/unknown stay neutral rather than implying either.
  get proximityTrendClass(): string {
    switch (this.proximityTrend) {
      case 'closing':
        return 'text-danger';
      case 'opening':
        return 'text-success';
      case 'steady':
        return 'text-warning';
      default:
        return '';
    }
  }

  private loadUserAndPair(): void {
    if (!this.endUserId || !this.threatPairId) {
      this.errorMessage = 'This threat pair could not be found.';
      return;
    }
    this.loading = true;
    this.userService
      .getUserById(this.endUserId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          this.user = response?.data;
          this.pair =
            (this.user?.threatPairs ?? []).find(
              (candidate: ThreatPair) => candidate.id === this.threatPairId,
            ) ?? null;
          if (!this.pair) {
            this.errorMessage = 'This threat pair could not be found.';
            return;
          }
          this.startPolling();
        },
        error: () => {
          this.errorMessage = 'Unable to load this threat pair right now.';
        },
      });
  }

  private startPolling(): void {
    this.loadLocations();
    this.stopPolling();
    this.refreshHandle = setInterval(
      () => this.loadLocations(true),
      NIGHTWEB_APP_CONSTANT.threatPairLiveMapRefreshMs,
    );
  }

  private stopPolling(): void {
    if (this.refreshHandle) {
      clearInterval(this.refreshHandle);
      this.refreshHandle = undefined;
    }
  }

  private getPartyId(user: ThreatPairUser | undefined): string | undefined {
    return (user as any)?._id ?? (user as any)?.id ?? undefined;
  }

  private loadLocations(isRefresh = false): void {
    const survivorId = this.getPartyId(this.pair?.survivor);
    const perpetratorId = this.getPartyId(this.pair?.perpetrator);

    if (!survivorId || !perpetratorId) {
      this.errorMessage = 'Location data is unavailable for this pair.';
      return;
    }

    const zoneRadiiMeters = this.zoneRadiiMeters;

    // Both parties are mocked for this demo (NEX-1534). Perpetrator GPS has no backend endpoint
    // at all yet. The survivor's real `last-locations` endpoint exists and still seeds the
    // geography below, but its result is itself wrapped in a gentle wander — see
    // `ThreatPairService.getSurvivorLocations` — so the Protected Party visibly moves too,
    // instead of sitting frozen at whatever their last real check-in happened to be. The
    // perpetrator then orbits around the survivor's just-resolved (mocked) position — see
    // `ThreatPairService.getPerpetratorLocations`.
    this.userService
      .getLocations(survivorId)
      .pipe(
        switchMap((rawSurvivorResponse: any) => {
          const anchorPosition = this.extractLatestPosition(
            rawSurvivorResponse?.data,
          );
          return this.threatPairService.getSurvivorLocations(
            survivorId,
            anchorPosition,
          );
        }),
        switchMap((survivorResponse: any) => {
          const referencePosition = this.extractLatestPosition(
            survivorResponse?.data,
          );
          return this.threatPairService
            .getPerpetratorLocations(
              perpetratorId,
              referencePosition,
              zoneRadiiMeters,
            )
            .pipe(
              map((perpetratorResponse: any) => ({
                survivorResponse,
                perpetratorResponse,
              })),
            );
        }),
      )
      .subscribe({
        next: ({ survivorResponse, perpetratorResponse }: any) => {
          this.applyLocations(
            survivorResponse?.data,
            perpetratorResponse?.data,
            zoneRadiiMeters,
          );
        },
        error: () => {
          if (!isRefresh) {
            this.errorMessage = 'Unable to load live locations right now.';
          }
        },
      });
  }

  private applyLocations(
    survivorData: any,
    perpetratorData: any,
    zoneRadiiMeters: ThreatPairZoneRadiiMeters,
  ): void {
    this.errorMessage = null;

    this.survivorSummary = this.buildPartySummary(
      'Survivor',
      this.pair?.survivor?.name ?? 'Survivor',
      survivorData,
    );
    this.perpetratorSummary = this.buildPartySummary(
      'Perpetrator',
      this.pair?.perpetrator?.name ?? 'Perpetrator',
      perpetratorData,
    );

    this.mapParties = [
      {
        label: this.survivorSummary?.name ?? 'Survivor',
        features: survivorData,
        color: SURVIVOR_HEADING_COLOR,
      },
      {
        label: this.perpetratorSummary?.name ?? 'Perpetrator',
        features: perpetratorData,
        color: PERPETRATOR_HEADING_COLOR,
      },
    ].filter((party) => party.features?.rows?.length);

    // BR-002/FR-405: both zone radii are centred on the Protected Party (Survivor) and move
    // with them — never on the pair midpoint used for the map's camera.
    this.zoneConfig = this.survivorSummary?.position
      ? {
          center: this.survivorSummary.position,
          yellowRadiusMeters: zoneRadiiMeters.yellow,
          redRadiusMeters: zoneRadiiMeters.red,
        }
      : null;

    if (!this.survivorSummary?.position || !this.perpetratorSummary?.position) {
      this.distanceMeters = null;
      this.distanceLabel = '—';
      this.proximityTrend = 'unknown';
      this.closingRateMps = null;
      this.zoneState = 'unknown';
      this.isHoldingStaleFix = false;
      this.previousDistanceMeters = null;
      return;
    }

    // FR-501: a stale fix must not drive a state transition. Hold the last good readouts and say
    // so, rather than asserting a fresh distance and zone from coordinates we don't trust.
    const isStaleFix =
      this.survivorSummary.isStale || this.perpetratorSummary.isStale;
    if (isStaleFix) {
      this.isHoldingStaleFix = this.distanceMeters != null;
      return;
    }
    this.isHoldingStaleFix = false;

    const currentDistance = haversineDistanceMeters(
      this.survivorSummary.position,
      this.perpetratorSummary.position,
    );
    this.distanceMeters = currentDistance;
    this.distanceLabel = `${Math.round(currentDistance)} m`;

    const previousZoneState = this.zoneState;
    this.zoneState = this.classifyZone(
      currentDistance,
      this.zoneState,
      zoneRadiiMeters,
    );
    if (this.zoneState !== previousZoneState) {
      this.logZoneTransition(previousZoneState, this.zoneState, currentDistance);
    }

    if (this.previousDistanceMeters != null) {
      // FR-403: a rate, not just a direction — how fast the gap is changing, in m/s.
      const closedMeters = this.previousDistanceMeters - currentDistance;
      const elapsedSeconds =
        NIGHTWEB_APP_CONSTANT.threatPairLiveMapRefreshMs / 1000;
      const rate = closedMeters / elapsedSeconds;
      this.closingRateMps = Math.round(rate * 10) / 10;

      const deadband = NIGHTWEB_APP_CONSTANT.axisLinkClosingRateDeadbandMps;
      this.proximityTrend =
        rate > deadband ? 'closing' : rate < -deadband ? 'opening' : 'steady';
    }
    this.previousDistanceMeters = currentDistance;
  }

  /**
   * Session-scoped activity log, matching the Figma mockup's event strip. Real transitions, not
   * backend history — resets when the page is left, since there's no persistence for this yet.
   */
  private logZoneTransition(
    from: AxisLinkZoneState,
    to: AxisLinkZoneState,
    distanceMeters: number,
  ): void {
    if (from === 'unknown') {
      return; // first resolved state isn't a "transition" worth logging
    }
    const perpName = this.perpetratorSummary?.name ?? 'Monitored subject';
    const distanceLabel = `~${Math.round(distanceMeters)}m`;
    const zoneLabel: Record<AxisLinkZoneState, string> = {
      red: 'Red Zone',
      yellow: 'Yellow Zone',
      outside: 'Outside Monitoring Zone',
      unknown: 'Unknown',
    };

    let line: string;
    if (to === 'outside') {
      line = `${perpName} returned to ${zoneLabel[to]} (${distanceLabel})`;
    } else {
      line = `${perpName} entered ${zoneLabel[to]} (${distanceLabel})`;
    }

    this.eventLog = [line, ...this.eventLog].slice(0, EVENT_LOG_MAX_ENTRIES);
  }

  /**
   * FR-206/BR-012/NFR-03: zone state with hysteresis. Entering a zone uses the plain radius, but
   * *leaving* one has to clear the radius by a buffer, so a subject loitering on a boundary
   * doesn't oscillate between tiers on every check-in. Red still takes precedence over Yellow
   * (BR-006). Mirrors `classify()` in the client's geo-proximity simulation.
   */
  private classifyZone(
    distanceMeters: number,
    previous: AxisLinkZoneState,
    radii: ThreatPairZoneRadiiMeters,
  ): AxisLinkZoneState {
    const fraction = NIGHTWEB_APP_CONSTANT.axisLinkZoneHysteresisBufferFraction;
    const redBuffer = Math.max(
      radii.red * fraction,
      NIGHTWEB_APP_CONSTANT.axisLinkZoneHysteresisMinMeters,
    );
    const yellowBuffer = Math.max(
      radii.yellow * fraction,
      NIGHTWEB_APP_CONSTANT.axisLinkZoneHysteresisMinMeters,
    );

    if (previous === 'red') {
      if (distanceMeters > radii.red + redBuffer) {
        return distanceMeters <= radii.yellow + yellowBuffer
          ? 'yellow'
          : 'outside';
      }
      return 'red';
    }

    if (previous === 'yellow') {
      if (distanceMeters <= radii.red) {
        return 'red';
      }
      return distanceMeters > radii.yellow + yellowBuffer ? 'outside' : 'yellow';
    }

    // No committed state yet, or currently outside: classify on the plain radii.
    if (distanceMeters <= radii.red) {
      return 'red';
    }
    return distanceMeters <= radii.yellow ? 'yellow' : 'outside';
  }

  private extractLatestPosition(data: any): GeoPoint | null {
    const rows = (data?.rows ?? []).filter(
      (row: any) =>
        row?.gps?.latitude &&
        row.gps.latitude !== 'null' &&
        row?.gps?.longitude &&
        row.gps.longitude !== 'null',
    );
    if (!rows.length) {
      return null;
    }
    const latest = rows[rows.length - 1];
    return {
      lat: parseFloat(latest.gps.latitude),
      lng: parseFloat(latest.gps.longitude),
    };
  }

  private buildPartySummary(
    role: 'Survivor' | 'Perpetrator',
    name: string,
    data: any,
  ): ThreatPairPartySummary {
    const rows = (data?.rows ?? []).filter(
      (row: any) =>
        row?.gps?.latitude &&
        row.gps.latitude !== 'null' &&
        row?.gps?.longitude &&
        row.gps.longitude !== 'null',
    );

    if (!rows.length) {
      return {
        role,
        name,
        position: null,
        headingLabel: null,
        headingDegrees: null,
        reportingLabel: 'Unavailable',
        isHighFrequency: false,
        lastUpdatedLabel: '—',
        hasLocation: false,
        isStale: true,
      };
    }

    const latest = rows[rows.length - 1];
    const previous = rows.length > 1 ? rows[rows.length - 2] : null;

    const position: GeoPoint = {
      lat: parseFloat(latest.gps.latitude),
      lng: parseFloat(latest.gps.longitude),
    };
    const previousPosition: GeoPoint | null = previous
      ? {
          lat: parseFloat(previous.gps.latitude),
          lng: parseFloat(previous.gps.longitude),
        }
      : null;

    const headingDegrees = previousPosition
      ? bearingDegrees(previousPosition, position)
      : null;

    const isHighFrequency = !!latest.isEmergencyState;

    return {
      role,
      name,
      position,
      headingLabel: headingDegrees != null ? compassDirection(headingDegrees) : null,
      headingDegrees,
      reportingLabel: isHighFrequency
        ? 'High-frequency (emergency mode)'
        : 'Standard',
      isHighFrequency,
      lastUpdatedLabel: this.formatRowTimestamp(latest),
      hasLocation: true,
      isStale: this.isRowStale(latest),
    };
  }

  /**
   * `last-locations` rows have no documented timestamp field in this frontend;
   * try the field names used elsewhere in this app before falling back.
   */
  private parseRowTimestamp(row: any): Date | null {
    const raw =
      row?.createdAt ??
      row?.recordedAt ??
      row?.checkInTime ??
      row?.timestamp ??
      row?.date ??
      row?.updatedAt;
    if (!raw) {
      return null;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatRowTimestamp(row: any): string {
    const parsed = this.parseRowTimestamp(row);
    if (!parsed) {
      return '—';
    }
    return parsed.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  /**
   * FR-502: "surface a staleness indicator on the display" when a fix is older than the
   * threshold. The exact threshold is unresolved with the client (BFRD OQ-05, blocks
   * development) — `axisLinkFallbackPositionStalenessMs` is a placeholder until confirmed. When
   * no timestamp can be parsed at all, treat the position as stale rather than assuming it's
   * fresh.
   */
  private isRowStale(row: any): boolean {
    const parsed = this.parseRowTimestamp(row);
    if (!parsed) {
      return true;
    }
    return (
      Date.now() - parsed.getTime() >
      NIGHTWEB_APP_CONSTANT.axisLinkFallbackPositionStalenessMs
    );
  }
}
